import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
// @mui
import Link from "@mui/material/Link";
import PropTypes from "prop-types";
// components
import ConsumanDataGrid, {
  Scrolling,
  LoadPanel,
  FilterRow,
  HeaderFilter,
  GroupPanel,
  Selection,
  Export,
  ColumnChooser,
  FilterPanel,
  ColumnFixing,
  StateStoring,
  Sorting,
  RemoteOperations,
  Paging,
} from "src/consuman-components/data-grid";
import Column from "src/consuman-components/generic/column";
import DxLocalStorage from "src/consuman-components/dx-local-storage";

//
import { FIELDTYPE } from "src/core/lib/constants";

// auth
import { useAuthContext } from "src/auth/hooks";

// @mui
import AlertDialog from "src/consuman-components/generic/alert";
// locales
import { useTranslation } from "react-i18next";

// format
import { dateTimeFormatGrid, numberFormat } from "src/core/lib/formats";

//
import { useGet as useGetDynamic } from "src/api/dynamic-configuration";
import ItemsSelectedHandler from "./items-selected-handler";

const EXPORT_FORMATS = ["xlsx", "pdf"];

export default function DataGrid({
  innerRef,
  isActive,
  deletedGender,
  columnName,
  columnCreatedDate,
  columnDeletedDate,
  gridActionRender,
  gridActionRenderWidth = 200,
  gridActionRenderMaxWidth = 200,
  gridActionHeaderRender,
  gridActionsHeaderRender,
  gridActionHeaderMultipleRender,
  gridActionHeaderToggleRender,
  gridActionHeaderInColumn,
  nameGrid,
  height = null,
  entityId,
  storageKey,
  dataSource,
  hrefName,
  type = "normal",
  onInitialized,
  remoteOperation = true,
  selectorMode = false,
  grouping = true,
  filtering = true,
  sorting = true,
  children,
  scrolling,
  columnsAutoWidth = true,
  onRowPrepared,
  ...props
}) {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const itemSelectionRef = useRef(null);

  const storageKeyEnterprise = storageKey ? `${user?.enterpriseCode}${storageKey}` : null;
  const initializeGridRef = useRef(true);
  const { dynamicConfigurations: dynamicConfigurationGet, isLoading } = useGetDynamic(entityId);
  const viewportHeightRatio = 0.83;

  const shouldUseViewportHeight = value => value === null || typeof value === "undefined";
  const calcViewportHeight = () => {
    if (typeof window === "undefined") return 600;
    return Math.round(window.innerHeight * viewportHeightRatio);
  };

  const [resolvedHeight, setResolvedHeight] = useState(() =>
    shouldUseViewportHeight(height) ? calcViewportHeight() : height
  );

  useEffect(() => {
    if (!shouldUseViewportHeight(height)) {
      setResolvedHeight(height);
      return undefined;
    }

    if (typeof window === "undefined") {
      setResolvedHeight(calcViewportHeight());
      return undefined;
    }

    const handleResize = () => {
      setResolvedHeight(calcViewportHeight());
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [height]);

  const dialogcomponent = useRef(false);

  const locale = useMemo(
    () => ({
      decimalSeparator: user?.numberFormat || ".",
      dateInputFormat: user?.dateFormat || "DD/MM/YYYY",
    }),
    [user?.numberFormat, user?.dateFormat]
  );

  const thisInitialized = e => {
    e.component.alertDialog = dialogcomponent;
    if (onInitialized) onInitialized(e);
  };

  const handleSelectionChanged = e => {
    itemSelectionRef?.current?.toggleShow(e?.selectedRowsData?.length);
    if (props.onSelectionChanged) props.onSelectionChanged(e);
  };

  const handleContentReady = e => {
    if (initializeGridRef.current) {
      initializeGridRef.current = false;
      e.component.clearSelection();

      if (props.selectedGrid) {
        e.component.clearFilter();

        if (props.selectRowsIds && props.selectRowsIds.length > 0) {
          e.component.selectRows(props.selectRowsIds, false);
          itemSelectionRef?.current?.toggleShow(props.selectRowsIds.length);
        }
      }
    }

    if (props.onContentReady) props.onContentReady(e);
  };

  const multipleSelectionRender = useCallback(
    () => (
      <ItemsSelectedHandler
        ref={itemSelectionRef}
        selectedItems={
          initializeGridRef.current
            ? 0
            : innerRef?.current?.instance?.getSelectedRowsData()?.length || 0
        }
        onClearSelected={() => {
          innerRef?.current?.instance?.clearSelection();
          itemSelectionRef?.current?.toggleShow(0);
        }}
        children={gridActionHeaderMultipleRender}
      />
    ),
    [gridActionHeaderMultipleRender, innerRef]
  );

  const toolbarItems = useMemo(() => {
    const list = [];

    if (selectorMode === "multiple")
      list.push({ location: "after", render: multipleSelectionRender });

    if (gridActionHeaderToggleRender)
      list.push({ location: "after", render: gridActionHeaderToggleRender });

    if (gridActionHeaderRender) list.push({ location: "after", render: gridActionHeaderRender });

    if (gridActionsHeaderRender)
      gridActionsHeaderRender.forEach(render => list.push({ location: "after", render }));

    if (storageKeyEnterprise) {
      list.push({
        location: "after",
        render: () => (
          <DxLocalStorage
            code={storageKeyEnterprise}
            dataGrid={innerRef}
          />
        ),
      });
      list.push({
        location: "after",
        render: () => (
          <DxLocalStorage
            load
            code={storageKeyEnterprise}
            dataGrid={innerRef}
          />
        ),
      });
    }

    return list;
  }, [
    gridActionHeaderRender,
    gridActionHeaderToggleRender,
    gridActionsHeaderRender,
    innerRef,
    multipleSelectionRender,
    selectorMode,
    storageKeyEnterprise,
  ]);

  return (
    <>
      {!isLoading ? (
        <>
          <AlertDialog ref={dialogcomponent} />
          <ConsumanDataGrid
            height={resolvedHeight}
            showColumnLines
            showRowLines
            allowColumnResizing
            dataSource={dataSource}
            width="100%"
            allowColumnReordering
            rowAlternationEnabled
            ref={innerRef}
            locale={locale}
            exportFileName={nameGrid}
            toolbarItems={toolbarItems}
            groupToggle={type === "normal" && grouping}
            {...props}
            onSelectionChanged={handleSelectionChanged}
            onRowPrepared={onRowPrepared}
            onInitialized={thisInitialized}
            onContentReady={handleContentReady}
            showBorders={false}
          >
            {!remoteOperation ? null : (
              <RemoteOperations
                paging
                filtering={filtering}
                sorting={sorting}
                grouping
                summary
                groupPaging
              />
            )}

            {!selectorMode ? null : (
              <Selection
                mode={selectorMode}
                allowSelectAll={false}
                selectAllMode="page"
              />
            )}

            <Paging
              enabled
              defaultPageSize={30}
            />
            <FilterRow visible={type === "normal" && filtering} />
            <HeaderFilter visible={type === "normal" && filtering} />
            <GroupPanel visible={type === "normal" && grouping} />
            <ColumnChooser enabled={type === "normal"} />
            <FilterPanel visible={type === "normal" && filtering} />
            <ColumnFixing enabled={type === "normal"} />
            <LoadPanel enabled={type === "normal"} />

            <Export
              enabled={type === "normal"}
              formats={EXPORT_FORMATS}
              allowExportSelectedData={false}
            />

            {storageKeyEnterprise ? (
              <StateStoring
                enabled
                type="localStorage"
                storageKey={storageKeyEnterprise}
              />
            ) : null}

            <Sorting mode={type === "normal" && sorting ? "single" : "none"} />

            {columnName ? (
              <Column
                caption={t("name")}
                dataField="name"
                dataType="string"
                sortOrder="asc"
                cellRender={
                  hrefName
                    ? e => (
                        <Link
                          href={hrefName(e.data.id)}
                          color="inherit"
                          underline="always"
                        >
                          {e.data.name}
                        </Link>
                      )
                    : null
                }
              />
            ) : null}

            {children}

            {isActive ? (
              <Column
                caption={deletedGender === "F" ? t("deleted") : t("deletedMasculine")}
                dataField="isActive"
                dataType="boolean"
                trueText={t("no")}
                falseText={t("yes")}
                cellRender={e => (e.value ? t("no") : t("yes"))}
                visible={false}
              />
            ) : null}

            {columnDeletedDate ? (
              <Column
                caption={t("deletedDate")}
                dataField="deletedDate"
                dataType="date"
                format={dateTimeFormatGrid(user?.dateTimeFormat)}
                allowGrouping={false}
                visible={false}
              />
            ) : null}

            {columnCreatedDate ? (
              <Column
                caption={t("createdDate")}
                dataField="createdDate"
                dataType="date"
                format={dateTimeFormatGrid(user?.dateTimeFormat)}
                allowGrouping={false}
                visible={false}
              />
            ) : null}

            {dynamicConfigurationGet.map(field => {
              const nameData = `dynamicField.${
                field.fieldName.charAt(0).toLowerCase() + field.fieldName.slice(1)
              }`;
              switch (field.type) {
                case FIELDTYPE.boolean:
                  return (
                    <Column
                      key={nameData}
                      caption={field.name}
                      dataField={nameData}
                      dataType="boolean"
                      trueText={t("yes")}
                      falseText={t("no")}
                      cellRender={e => (e.value ? t("yes") : t("no"))}
                      visible={false}
                    />
                  );
                case FIELDTYPE.text:
                  return (
                    <Column
                      key={nameData}
                      caption={field.name}
                      dataField={nameData}
                      dataType="string"
                      visible={false}
                    />
                  );
                case FIELDTYPE.number:
                  return (
                    <Column
                      key={nameData}
                      caption={field.name}
                      dataField={nameData}
                      format={numberFormat(user?.numberFormat)}
                      dataType="number"
                      alignment="right"
                      visible={false}
                    />
                  );
                case FIELDTYPE.selectMultiple:
                  return null;
                case FIELDTYPE.select:
                  return (
                    <Column
                      key={nameData}
                      caption={field.name}
                      dataField={nameData}
                      dataType="string"
                      visible={false}
                    />
                  );
                case FIELDTYPE.dateTime:
                  return (
                    <Column
                      key={nameData}
                      caption={field.name}
                      dataField={nameData}
                      format={dateTimeFormatGrid(user?.dateTimeFormat)}
                      dataType="date"
                      visible={false}
                    />
                  );
                default:
                  return null;
              }
            })}

            {gridActionRender ? (
              <Column
                caption=""
                minWidth={gridActionRenderWidth}
                allowFiltering={false}
                width={gridActionRenderMaxWidth}
                allowSorting={false}
                dataType="string"
                allowExporting={false}
                allowHiding={false}
                showInColumnChooser={false}
                fixed
                fixedPosition="right"
                cellRender={gridActionRender}
                headerCellRender={gridActionHeaderInColumn}
              />
            ) : null}

            {scrolling ? <Scrolling mode={scrolling} /> : null}
          </ConsumanDataGrid>
        </>
      ) : null}
    </>
  );
}

DataGrid.propTypes = {
  innerRef: PropTypes.any,
  gridActionRender: PropTypes.any,
  gridActionRenderWidth: PropTypes.any,
  gridActionRenderMaxWidth: PropTypes.any,
  gridActionHeaderRender: PropTypes.any,
  gridActionsHeaderRender: PropTypes.any,
  gridActionHeaderToggleRender: PropTypes.any,
  gridActionHeaderMultipleRender: PropTypes.any,
  gridActionHeaderInColumn: PropTypes.any,
  onInitialized: PropTypes.any,
  remoteOperation: PropTypes.bool,
  selectorMode: PropTypes.any,
  isActive: PropTypes.bool,
  deletedGender: PropTypes.oneOf(["M", "F"]),
  entityId: PropTypes.any,
  hrefName: PropTypes.any,
  columnName: PropTypes.bool,
  dataSource: PropTypes.any,
  onContentReady: PropTypes.any,
  selectedGrid: PropTypes.bool,
  selectRowsIds: PropTypes.any,
  grouping: PropTypes.bool,
  filtering: PropTypes.bool,
  sorting: PropTypes.bool,
  columnCreatedDate: PropTypes.bool,
  columnDeletedDate: PropTypes.bool,
  storageKey: PropTypes.string,
  nameGrid: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  type: PropTypes.string,
  children: PropTypes.any,
  scrolling: PropTypes.string,
  columnsAutoWidth: PropTypes.bool,
  onSelectionChanged: PropTypes.func,
  onRowPrepared: PropTypes.func,
  defaultFilterValue: PropTypes.any,
};
